import { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle2, Mail, ShieldCheck } from "lucide-react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "./AuthProvider";
import { supabase } from "../lib/supabase";

// Invitation emails link to /login#invite=<email>. The fragment keeps the
// address out of server and CDN logs while letting the page prefill it.
function invitedEmailFromHash() {
  const match = window.location.hash.match(/^#invite=(.+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]).trim().toLowerCase();
  } catch {
    return match[1].trim().toLowerCase();
  }
}

export function LoginPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invited] = useState(invitedEmailFromHash);
  const [mode, setMode] = useState<"login" | "signup">(searchParams.get("mode") === "signup" && !invited ? "signup" : "login");
  const [clientKey, setClientKey] = useState("");
  const [email, setEmail] = useState(invited);
  const [businessName, setBusinessName] = useState("");
  const [token, setToken] = useState("");
  const [sent, setSent] = useState(Boolean(invited));
  const [hasCode, setHasCode] = useState(Boolean(invited));
  useEffect(() => {
    if (invited) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, [invited]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [bootstrapRetry, setBootstrapRetry] = useState(false);

  if (session && !busy && !bootstrapRetry) return <Navigate to="/app" replace />;

  const switchMode = (nextMode: "login" | "signup") => {
    setMode(nextMode);
    setSearchParams(nextMode === "signup" ? { mode: "signup" } : {});
    setSent(false);
    setHasCode(false);
    setToken("");
    setError("");
    setNotice("");
    setBootstrapRetry(false);
  };

  const bootstrapAccount = async (accessToken: string) => {
    const response = await fetch("/api/ads/account-bootstrap", {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    });
    const payload = await response.json().catch(() => ({})) as { message?: string };
    if (!response.ok) throw new Error(payload.message || "Your ad account could not be created.");
  };

  const sendCode = async () => {
    if (!supabase) throw new Error("Authentication is not configured yet.");
    const normalized = email.trim().toLowerCase();
    if (!normalized) throw new Error("Enter your email address.");
    if (mode === "signup" && !businessName.trim()) throw new Error("Enter your business name.");
    if (mode === "signup" && !clientKey.trim()) throw new Error("Enter your Slack channel name.");
    const { error: sendError } = await supabase.auth.signInWithOtp({
      email: normalized,
      options: {
        shouldCreateUser: mode === "signup",
        ...(mode === "signup" ? { data: { business_name: businessName.trim(), client_key: clientKey.trim() } } : {}),
      },
    });
    if (sendError) throw sendError;
    setEmail(normalized);
    setSent(true);
    setHasCode(false);
    setNotice(`${mode === "signup" ? "An account verification" : "A secure sign-in"} code was sent. Only the newest code will work.`);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    let emailVerified = false;
    try {
      if (!sent) {
        await sendCode();
      } else {
        if (!supabase) throw new Error("Authentication is not configured yet.");
        if (token.length !== 6) throw new Error("Enter the 6-digit code.");
        // Invitation emails carry an invite-type code, so when the regular
        // verification rejects it, retry the same code as an invite token.
        const emailAttempt = await supabase.auth.verifyOtp({ email, token, type: "email" });
        let data = emailAttempt.data;
        if (emailAttempt.error) {
          const invite = await supabase.auth.verifyOtp({ email, token, type: "invite" });
          if (invite.error) throw emailAttempt.error;
          data = invite.data;
        }
        if (!data.session?.access_token) throw new Error("Your email was verified, but no session was created. Request a new code.");
        emailVerified = true;
        await bootstrapAccount(data.session.access_token);
        navigate("/app", { replace: true });
      }
    } catch (caught) {
      if (emailVerified) setBootstrapRetry(true);
      const message = caught instanceof Error ? caught.message : "Sign-in could not be completed.";
      setError(/banned/i.test(message) ? "This account is suspended. Contact Orange Trail to have access restored." : message);
    } finally {
      setBusy(false);
    }
  };

  const retryBootstrap = async () => {
    if (!session?.access_token) return;
    setBusy(true);
    setError("");
    try {
      await bootstrapAccount(session.access_token);
      setBootstrapRetry(false);
      navigate("/app", { replace: true });
    } catch (caught) {
      setBootstrapRetry(true);
      setError(caught instanceof Error ? caught.message : "Your ad account could not be created.");
    } finally { setBusy(false); }
  };

  const useExistingCode = () => {
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      setError("Enter your email address first.");
      return;
    }
    setEmail(normalized);
    setHasCode(true);
    setSent(true);
    setError("");
    setNotice("");
  };

  const resend = async () => {
    setBusy(true);
    setError("");
    try {
      await sendCode();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "A new code could not be sent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-story">
        <Logo inverse />
        <div className="login-story__copy">
          <span className="eyebrow eyebrow--light">ORANGE TRAIL ADS</span>
          <h1>Scale campaigns.<br />Stay compliant.<br /><em>Keep moving.</em></h1>
          <div className="login-proof">
            <span><CheckCircle2 /> Campaigns and reporting in one place</span>
            <span><CheckCircle2 /> Billing and pages in one workflow</span>
            <span><CheckCircle2 /> Hands-on support from Orange Trail</span>
          </div>
        </div>
        <p>orangetrail.app</p>
      </section>
      <section className="login-panel">
        <Link className="back-link" to="/"><ArrowLeft /> Back to Orange Trail</Link>
        <div className="login-card">
          {!sent && !bootstrapRetry && <div className="auth-mode" role="tablist" aria-label="Account access">
            <button role="tab" aria-selected={mode === "login"} className={mode === "login" ? "is-active" : ""} type="button" onClick={() => switchMode("login")}>Sign in</button>
            <button role="tab" aria-selected={mode === "signup"} className={mode === "signup" ? "is-active" : ""} type="button" onClick={() => switchMode("signup")}>Create account</button>
          </div>}
          <div className="login-icon">{sent ? <ShieldCheck /> : <Mail />}</div>
          <span className="eyebrow">{bootstrapRetry ? "EMAIL VERIFIED" : sent ? "CHECK YOUR EMAIL" : mode === "signup" ? "NEW CLIENT ACCOUNT" : "CLIENT ACCESS"}</span>
          <h2>{bootstrapRetry ? "Finish account setup" : sent ? "Enter your code" : mode === "signup" ? "Create your account" : "Welcome back"}</h2>
          <p>{bootstrapRetry ? "Your email is verified. Retry the secure ad account connection to continue." : sent ? (hasCode ? <>Enter the six-digit code from your email for <strong>{email}</strong>.</> : <>We sent six digits to <strong>{email}</strong>.</>) : mode === "signup" ? "Verify your work email to create your Orange Trail Ads account." : "Sign in with your work email. No password required."}</p>
          {error && <div className="form-alert" role="alert">{error}</div>}
          {notice && <div className="form-alert form-alert--success">{notice}</div>}
          {bootstrapRetry ? <button className="button button--primary button--wide" type="button" onClick={() => void retryBootstrap()} disabled={busy}>{busy ? "Creating account…" : "Retry account setup"}</button> : <form onSubmit={submit}>
            {!sent ? (
              <>
                {mode === "signup" && <label><span>Business name</span><input value={businessName} onChange={(event) => setBusinessName(event.target.value)} placeholder="Your company" autoComplete="organization" required /></label>}
                {mode === "signup" && <label><span>Slack channel</span><input value={clientKey} onChange={(event) => setClientKey(event.target.value)} placeholder="your-channel-name" autoCapitalize="none" autoCorrect="off" spellCheck={false} required /><small className="field-hint">The Orange Trail Slack channel you share with us. It links your team to the right client.</small></label>}
                <label><span>Work email</span><input value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@company.com" type="email" autoComplete="email" required autoFocus /></label>
              </>
            ) : (
              <label><span>One-time code</span><input className="otp-input" value={token} onChange={(event) => setToken(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" inputMode="numeric" autoComplete="one-time-code" required autoFocus /></label>
            )}
            <button className="button button--primary button--wide" type="submit" disabled={busy || (sent && token.length !== 6)}>{busy ? (sent ? mode === "signup" ? "Creating account…" : "Signing in…" : "Sending code…") : sent ? (mode === "signup" ? "Verify and create account" : "Verify and sign in") : mode === "signup" ? "Email me a verification code" : "Email me a sign-in code"}</button>
          </form>}
          {!sent && !bootstrapRetry && mode === "login" && <div className="login-actions"><button type="button" onClick={useExistingCode} disabled={busy}>Already have a code? Enter it</button></div>}
          {sent && <div className="login-actions"><button type="button" onClick={resend} disabled={busy}>{hasCode ? "Email me a fresh code" : "Resend code"}</button><button type="button" onClick={() => { setSent(false); setHasCode(false); setToken(""); setNotice(""); }}>Change email</button></div>}
          <p className="login-legal">By continuing, you agree to Orange Trail’s <Link to="/terms">Ads Terms</Link> and acknowledge its privacy policy.</p>
        </div>
      </section>
    </main>
  );
}
